import type { EmblaOptionsType } from 'embla-carousel';
import { classes } from 'html-classes';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Skeleton from 'react-loading-skeleton';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { IconButton, SIZE } from '~/components/isolated/IconButton';
import { useDotButton } from '~/hooks/useDotButton';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';

import { cfSrcForImage } from '../CategoryBlocks/utils';
import styles from './ProductImageSlider.module.scss';

interface ProductImageSliderProps
  extends React.ComponentPropsWithoutRef<'div'>,
    Pick<CarouselStackProps, 'badgesContent' | 'isZoomable'> {
  productTitle?: string;
  images?: string[];
  size?: {
    width: number;
  };
  canOpenFullScreen?: boolean;
  thumbsPlacement?: 'bottom' | 'left';
  badgesContent?: React.ReactElement<React.ComponentPropsWithoutRef<'div'>>;
  isLoading?: boolean;
  isZoomable?: boolean;
}

const ProductImageSlider: React.FC<ProductImageSliderProps> = ({
  productTitle,
  images = [],
  size,
  badgesContent,
  isZoomable = false,
  canOpenFullScreen = true,
  thumbsPlacement = 'bottom',
  isLoading = false,
  className,
  ...props
}) => {
  const { isMobile } = useGlobal();
  const isVerticalThumbs = !isMobile && thumbsPlacement === 'left';

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const [mainCarouselApi, setMainCarouselApi] = useState<CarouselApi>();
  const [thumbCarouselApi, setThumbCarouselApi] = useState<CarouselApi>();

  const onSelect = useCallback(() => {
    if (!mainCarouselApi || !thumbCarouselApi) {
      return;
    }

    setSelectedIndex(mainCarouselApi.selectedScrollSnap());
    thumbCarouselApi.scrollTo(mainCarouselApi.selectedScrollSnap());
  }, [mainCarouselApi, thumbCarouselApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!mainCarouselApi || !thumbCarouselApi) {
        return;
      }

      mainCarouselApi.scrollTo(index);
      setSelectedIndex(index);
    },
    [mainCarouselApi, thumbCarouselApi],
  );

  const onSlideClick = useCallback(
    (index: number) => {
      if (!canOpenFullScreen) {
        return;
      }

      setSelectedIndex(index);
      setIsFullscreen(true);
    },
    [canOpenFullScreen],
  );

  const onClose = useCallback(() => setIsFullscreen(false), []);

  const desktopCarouselStackProps: Omit<CarouselStackProps, 'onSlideClick'> = {
    images,
    productTitle,
    size,
    selectedIndex,
    mainCarouselApi,
    setMainCarouselApi,
    setThumbCarouselApi,
    onThumbClick,
  };

  useEffect(() => {
    if (!mainCarouselApi) {
      return;
    }
    onSelect();

    mainCarouselApi.on('select', onSelect).on('reInit', onSelect);
  }, [mainCarouselApi, onSelect]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('body-no-scroll');
    } else {
      document.body.classList.remove('body-no-scroll');
    }
  }, [isFullscreen]);

  if (isLoading) {
    const skeletonThumbs = Array.from(
      { length: isVerticalThumbs ? 6 : 5 },
      (_, index) => `thumb-skeleton-${index}`,
    );

    return (
      <div
        {...props}
        className={classes([styles.productImageSlider, className])}
      >
        <div
          className={classes([
            styles.skeletonStack,
            isVerticalThumbs && styles.skeletonStackVertical,
          ])}
        >
          {isVerticalThumbs && !isMobile && (
            <div
              className={classes([
                styles.skeletonThumbs,
                styles.skeletonThumbsVertical,
              ])}
            >
              {skeletonThumbs.map((thumbKey) => (
                <div
                  key={thumbKey}
                  className={classes([
                    styles.skeletonThumb,
                    styles.skeletonThumbVertical,
                  ])}
                >
                  <Skeleton className={styles.skeletonThumbItem} />
                </div>
              ))}
            </div>
          )}
          <div
            className={classes([
              styles.skeletonMain,
              isVerticalThumbs && styles.skeletonMainVertical,
            ])}
          >
            <Skeleton className={styles.skeletonMainImage} />
          </div>
          {!isVerticalThumbs && !isMobile && (
            <div className={styles.skeletonThumbs}>
              {skeletonThumbs.map((thumbKey) => (
                <div key={thumbKey} className={styles.skeletonThumb}>
                  <Skeleton className={styles.skeletonThumbItem} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div {...props} className={classes([styles.productImageSlider, className])}>
      {isFullscreen ? (
        createPortal(
          <div className={styles.fullScreenWrapper} data-fullscreen-wrapper>
            {!isMobile && <h2>{productTitle}</h2>}
            <IconButton
              icon="close"
              size={SIZE.L}
              variant="outline"
              onClick={onClose}
            />
            <CarouselStack
              {...desktopCarouselStackProps}
              showArrows
              thumbsPlacement="bottom"
            />
          </div>,
          document.body,
        )
      ) : (
        <CarouselStack
          {...desktopCarouselStackProps}
          isZoomable={isZoomable}
          badgesContent={badgesContent}
          onSlideClick={onSlideClick}
          thumbsPlacement={thumbsPlacement}
        />
      )}
    </div>
  );
};

interface CarouselStackProps
  extends Pick<ProductImageSliderProps, 'images' | 'productTitle' | 'size'> {
  badgesContent?: React.ReactElement<React.ComponentPropsWithoutRef<'div'>>;
  selectedIndex: number;
  showArrows?: boolean;
  mainCarouselApi?: CarouselApi;
  setMainCarouselApi: (api: CarouselApi) => void;
  setThumbCarouselApi: (api: CarouselApi) => void;
  onThumbClick: (index: number) => void;
  onSlideClick?: (index: number) => void;
  thumbsPlacement?: 'bottom' | 'left';
  isZoomable?: boolean;
}

const CarouselStack = memo<CarouselStackProps>(
  ({
    images = [],
    productTitle,
    size,
    badgesContent,
    selectedIndex,
    showArrows,
    mainCarouselApi,
    setMainCarouselApi,
    setThumbCarouselApi,
    onThumbClick,
    onSlideClick,
    thumbsPlacement = 'bottom',
    isZoomable: isZoomableProp = false,
  }) => {
    const { isMobile } = useGlobal();
    const isVerticalThumbs = !isMobile && thumbsPlacement === 'left';

    const {
      onPrevButtonClick,
      onNextButtonClick,
      prevBtnDisabled,
      nextBtnDisabled,
    } = usePrevNextButtons(mainCarouselApi);

    const {
      scrollSnaps,
      selectedIndex: dotSelectedIndex,
      onDotButtonClick,
    } = useDotButton(mainCarouselApi);

    const isCarouselActive = images.length > 1;
    const isZoomable = !isMobile && isZoomableProp;

    const [opts] = useState<EmblaOptionsType>({
      startIndex: selectedIndex,
    });

    // Zoom lens state
    const [isZoomActive, setIsZoomActive] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const [currentZoomImage, setCurrentZoomImage] = useState<string | null>(
      null,
    );
    const [zoomImagePosition, setZoomImagePosition] = useState({ x: 0, y: 0 });
    const [imageNaturalSize, setImageNaturalSize] = useState<{
      width: number;
      height: number;
    } | null>(null);

    const itemRef = useRef<HTMLDivElement | null>(null);

    const handleMouseEnter = useCallback(
      (image: string, event: React.MouseEvent<HTMLDivElement>) => {
        if (isMobile) {
          return;
        }
        setCurrentZoomImage(image);
        setIsZoomActive(true);

        // Initialize zoom position immediately
        const currentTarget = event.currentTarget as HTMLDivElement;
        const imgElement = currentTarget.querySelector(
          'img',
        ) as HTMLImageElement | null;

        if (imgElement) {
          // Get natural size of the image to avoid distortion
          const naturalWidth = imgElement.naturalWidth || imgElement.width;
          const naturalHeight = imgElement.naturalHeight || imgElement.height;

          if (naturalWidth > 0 && naturalHeight > 0) {
            setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
          }

          const imageRect = imgElement.getBoundingClientRect();
          const imageX = event.clientX - imageRect.left;
          const imageY = event.clientY - imageRect.top;
          const percentX = (imageX / imageRect.width) * 100;
          const percentY = (imageY / imageRect.height) * 100;

          setZoomPosition({ x: event.clientX, y: event.clientY });
          setZoomImagePosition({
            x: Math.max(0, Math.min(100, percentX)),
            y: Math.max(0, Math.min(100, percentY)),
          });
        }
      },
      [isMobile],
    );

    const handleMouseLeave = useCallback(() => {
      setIsZoomActive(false);
      setCurrentZoomImage(null);
      setImageNaturalSize(null);
    }, []);

    const handleImageLoad = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        const imgElement = event.currentTarget;
        const naturalWidth = imgElement.naturalWidth || imgElement.width;
        const naturalHeight = imgElement.naturalHeight || imgElement.height;

        if (naturalWidth > 0 && naturalHeight > 0) {
          setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
        }
      },
      [],
    );

    const handleMouseMove = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (isMobile) {
          return;
        }

        if (!isZoomActive || !currentZoomImage) {
          return;
        }

        const currentTarget = event.currentTarget as HTMLDivElement;
        const imgElement = currentTarget.querySelector(
          'img',
        ) as HTMLImageElement | null;

        if (!imgElement) {
          return;
        }

        // Update natural size if not set yet
        if (!imageNaturalSize) {
          const naturalWidth = imgElement.naturalWidth || imgElement.width;
          const naturalHeight = imgElement.naturalHeight || imgElement.height;

          if (naturalWidth > 0 && naturalHeight > 0) {
            setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
          }
        }

        const imageRect = imgElement.getBoundingClientRect();

        // Calculate mouse position relative to the viewport for zoom lens positioning
        const x = event.clientX;
        const y = event.clientY;

        // Calculate mouse position relative to the image
        const imageX = event.clientX - imageRect.left;
        const imageY = event.clientY - imageRect.top;

        // Calculate percentage position within the image
        const percentX = (imageX / imageRect.width) * 100;
        const percentY = (imageY / imageRect.height) * 100;

        // Clamp values between 0 and 100
        const clampedX = Math.max(0, Math.min(100, percentX));
        const clampedY = Math.max(0, Math.min(100, percentY));

        setZoomPosition({ x, y });
        setZoomImagePosition({ x: clampedX, y: clampedY });
      },
      [isMobile, isZoomActive, currentZoomImage, imageNaturalSize],
    );

    // Calculate zoom image size based on natural image size (2x larger)
    const zoomImageSize = useMemo(() => {
      if (
        imageNaturalSize &&
        imageNaturalSize.width > 0 &&
        imageNaturalSize.height > 0
      ) {
        return {
          width: imageNaturalSize.width * 2,
          height: imageNaturalSize.height * 2,
        };
      }

      // Fallback to size prop if natural size is not available
      return size
        ? {
            width: size.width * 2,
          }
        : undefined;
    }, [imageNaturalSize, size]);

    /**
     * Builds the lens image URL from the slot width rather than from the loaded
     * image's natural size. The natural size is only known once the slide has
     * decoded, which used to make the URL unknowable until the pointer arrived —
     * so the download started on hover and the reader watched it arrive. Keyed on
     * the slot instead, the URL is stable from mount and can be warmed ahead of
     * time. imgproxy never upscales, so a source narrower than the request still
     * returns the same pixels it does today; only `backgroundSize` below stays on
     * the natural size, because that is what sets the zoom factor.
     */
    const buildZoomUrl = useCallback(
      (image: string) =>
        size ? cfSrcForImage(image, { width: size.width * 2 }) : null,
      [size],
    );

    const zoomImageUrl = useMemo(
      () => (currentZoomImage ? buildZoomUrl(currentZoomImage) : null),
      [currentZoomImage, buildZoomUrl],
    );

    /**
     * Warms the lens image for the slide in view so hovering it is instant.
     * Deferred to idle time: the lens is a second, larger copy of an image the
     * reader may never zoom, and it must not compete with the slide itself for
     * bandwidth while the page is still painting. Desktop only — `isZoomable`
     * is false on touch, where there is no hover to warm up for.
     */
    useEffect(() => {
      if (!isZoomable) {
        return;
      }
      const image = images[selectedIndex];
      const url = image ? buildZoomUrl(image) : null;
      if (!url) {
        return;
      }

      const warm = () => {
        const preloader = new Image();
        preloader.src = url;
      };

      if (typeof requestIdleCallback === 'function') {
        const handle = requestIdleCallback(warm, { timeout: 2000 });
        return () => cancelIdleCallback(handle);
      }

      const handle = window.setTimeout(warm, 500);
      return () => window.clearTimeout(handle);
    }, [isZoomable, images, selectedIndex, buildZoomUrl]);

    // Reset zoom and image size when selected image changes
    useEffect(() => {
      setIsZoomActive(false);
      setCurrentZoomImage(null);
      setImageNaturalSize(null);
    }, [selectedIndex]);

    return (
      <div
        className={classes([
          styles.carouselStack,
          isVerticalThumbs && styles.carouselStackVertical,
        ])}
      >
        {showArrows && isCarouselActive && !isMobile && (
          <>
            <IconButton
              icon="chevron-3.1"
              size={SIZE.XL}
              variant="outline"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              className={styles.mainCarouselPrevButton}
            />
            <IconButton
              icon="chevron-3.1"
              size={SIZE.XL}
              variant="outline"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              className={styles.mainCarouselNextButton}
            />
          </>
        )}
        {isVerticalThumbs && !isMobile && isCarouselActive && (
          <Carousel
            opts={{
              ...opts,
              containScroll: 'keepSnaps',
              dragFree: true,
            }}
            setApi={setThumbCarouselApi}
            orientation="vertical"
            className={classes([
              styles.thumbCarousel,
              styles.thumbCarouselVertical,
            ])}
            data-carousel="thumb"
          >
            <CarouselContent className={styles.thumbCarouselContent}>
              {images.map((image, index) => (
                <CarouselItem
                  key={image}
                  onClick={() => onThumbClick(index)}
                  className={classes([
                    styles.thumbCarouselItem,
                    selectedIndex === index && styles.thumbCarouselItemSelected,
                  ])}
                >
                  <CloudflareResponseImage
                    data-image={`slider-image-${index}`}
                    src={image}
                    srcParams={{ width: 128 }}
                    srcSetParams={defaultSrcSetParams}
                    alt={productTitle}
                    aria-label="Select image"
                    className={styles.thumbCarouselImage}
                    loading="eager"
                    fetchPriority="low"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        )}

        <Carousel
          opts={{ ...opts, active: isCarouselActive }}
          setApi={setMainCarouselApi}
          className={classes([
            styles.mainCarousel,
            isVerticalThumbs && styles.mainCarouselWithThumbs,
          ])}
          data-carousel="main"
        >
          {badgesContent}
          <CarouselContent className={styles.mainCarouselContent}>
            {images.map((image, index) => {
              const isCurrentImage = index === selectedIndex;
              return (
                <CarouselItem
                  key={image}
                  data-index={index}
                  onClick={() => onSlideClick?.(index)}
                  className={styles.mainCarouselItem}
                  ref={isCurrentImage ? itemRef : undefined}
                  onMouseEnter={(e) => isZoomable && handleMouseEnter(image, e)}
                  onMouseLeave={isZoomable ? handleMouseLeave : undefined}
                  onMouseMove={isZoomable ? handleMouseMove : undefined}
                >
                  <CloudflareResponseImage
                    src={image}
                    data-image="slider-image-current"
                    srcParams={size}
                    srcSetParams={defaultSrcSetParams}
                    alt="Product image"
                    className={classes([
                      styles.mainCarouselImage,
                      isZoomable && styles.mainCarouselImageZoomable,
                    ])}
                    aria-label="Select image"
                    // Every slide is fetched up front so swiping and the zoom
                    // lens never wait on a download, but only the one on screen
                    // competes for bandwidth: the rest are explicitly deprioritized
                    // so they queue behind the LCP image instead of racing it.
                    loading="eager"
                    fetchPriority={index === 0 ? 'high' : 'low'}
                    onLoad={handleImageLoad}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {isZoomable && isZoomActive && zoomImageUrl && (
            <div
              className={styles.zoomLens}
              style={{
                left: `${zoomPosition.x}px`,
                top: `${zoomPosition.y}px`,
              }}
            >
              <div
                className={styles.zoomLensImage}
                style={{
                  backgroundImage: `url(${zoomImageUrl})`,
                  backgroundPosition: `${zoomImagePosition.x}% ${zoomImagePosition.y}%`,
                  backgroundSize: zoomImageSize
                    ? `${zoomImageSize.width}px ${
                        zoomImageSize.height
                          ? `${zoomImageSize.height}px`
                          : 'auto'
                      }`
                    : '200%',
                }}
              />
            </div>
          )}
        </Carousel>

        {isMobile
          ? isCarouselActive && (
              <div className={styles.mainCarouselDots} data-carousel-dots>
                {scrollSnaps.map((_, index) => (
                  <span
                    key={`dot-${index}-${scrollSnaps.length}`}
                    onClick={() => onDotButtonClick(index)}
                    className={classes([
                      styles.mainCarouselDot,
                      index === dotSelectedIndex && styles.active,
                    ])}
                  />
                ))}
              </div>
            )
          : !isVerticalThumbs &&
            isCarouselActive && (
              <Carousel
                opts={{
                  ...opts,
                  containScroll: 'keepSnaps',
                  dragFree: true,
                }}
                setApi={setThumbCarouselApi}
                orientation="horizontal"
                className={styles.thumbCarousel}
                data-carousel="thumb"
              >
                <CarouselContent className={styles.thumbCarouselContent}>
                  {images.map((image, index) => (
                    <CarouselItem
                      key={image}
                      onClick={() => onThumbClick(index)}
                      className={classes([
                        styles.thumbCarouselItem,
                        selectedIndex === index &&
                          styles.thumbCarouselItemSelected,
                      ])}
                    >
                      <CloudflareResponseImage
                        data-image={`slider-image-${index}`}
                        src={image}
                        srcParams={{ width: 128 }}
                        srcSetParams={defaultSrcSetParams}
                        alt={productTitle}
                        aria-label="Select image"
                        className={styles.thumbCarouselImage}
                        loading="eager"
                        fetchPriority="low"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            )}
      </div>
    );
  },
);

CarouselStack.displayName = 'CarouselStack';

export default ProductImageSlider;
